import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';
import { GraduationCap, CalendarBlank, Trash, WarningCircle } from '@phosphor-icons/react';
import { RegistrationTab } from '../components/acad/RegistrationTab';
import { GradingTab } from '../components/acad/GradingTab';
import { HierarchyTab } from '../components/acad/HierarchyTab';
import { CustomizationTab } from '../components/acad/CustomizationTab';
import { CustomFields } from '../components/acad/CustomFields';

const MANAGE_ROLES = ['global_admin', 'school_admin', 'principal', 'registrar'];
const useCanManage = () => {
  const { user } = useAuth();
  return MANAGE_ROLES.includes(user?.role);
};

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const Sel = (props) => <select {...props} className={`border border-slate-200 rounded-md px-2 py-2 text-sm bg-white ${props.className || ''}`} />;
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function useRef_(kind) {
  return useQuery({ queryKey: ['ref', kind], queryFn: () => api.get(`/acad/refdata/${kind}`).then((r) => r.data) });
}
const refValues = (d) => (d?.values || []).filter((v) => v.active).map((v) => v.value);

function Overview({ setTab }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['acad-overview'], queryFn: () => api.get('/acad/overview').then((r) => r.data) });
  const pick = async (model) => {
    try { await api.put('/acad/model', { model }); toast.success('Academic model set'); qc.invalidateQueries({ queryKey: ['acad-overview'] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      {!data?.model && (
        <Card testid="acad-model-picker">
          <p className="font-display font-bold mb-1">Choose your academic model</p>
          <p className="text-sm text-slate-500 mb-3">This shapes your institution structure. You can rename levels later in settings.</p>
          <div className="flex gap-2">
            <Button data-testid="acad-model-k12" className="bg-slate-900 hover:bg-slate-800" onClick={() => pick('k12')}>School (K-12)</Button>
            <Button data-testid="acad-model-university" variant="outline" onClick={() => pick('university')}>University (multi-campus)</Button>
          </div>
        </Card>
      )}
      {data?.model && <Card testid="acad-model-current"><p className="text-sm"><b className="capitalize">{data.model.model}</b> model · levels: {data.model.levels.join(' > ')}</p></Card>}
      <div className="grid sm:grid-cols-5 gap-3" data-testid="acad-counts">
        {Object.entries(data?.counts || {}).map(([k, v]) => (
          <button key={k} onClick={() => setTab(k === 'terms' ? 'terms' : k === 'programmes' ? 'programmes' : k === 'courses' ? 'courses' : k === 'sections' ? 'sections' : 'overview')}
            className="bg-white border border-slate-200 rounded-xl p-4 text-start hover:border-orange-300 transition-colors">
            <p className="font-mono-label text-[10px] uppercase text-slate-400">{k.replace('_', ' ')}</p>
            <p className="font-display text-2xl font-extrabold">{v}</p>
          </button>
        ))}
      </div>
      <Card testid="acad-active-terms">
        <p className="font-display font-bold mb-2">Terms running today</p>
        {(data?.active_terms || []).length === 0 && <p className="text-sm text-slate-400">No term covers today - create one under Terms.</p>}
        {(data?.active_terms || []).map((t) => <p key={t.code} className="text-sm py-1">{t.code} · {t.name} · <span className="capitalize">{t.status}</span> · ends {t.end}</p>)}
      </Card>
    </div>
  );
}

function Terms() {
  const canManage = useCanManage();
  const qc = useQueryClient();
  const { data: termTypes } = useRef_('term_types');
  const { data: terms } = useQuery({ queryKey: ['acad-terms'], queryFn: () => api.get('/acad/terms').then((r) => r.data) });
  const [f, setF] = useState({ code: '', name: '', term_type: 'Semester', start: '', end: '', registration_open: '', registration_close: '', drop_add_open: '', drop_add_close: '', grading_open: '', grading_close: '', census_date: '', status: 'draft', custom: {}, pw: '' });
  const save = async () => {
    try {
      const priority_windows = f.pw.split('\n').filter(Boolean).map((ln) => {
        const [level, open, close] = ln.split(',').map((x) => x.trim());
        return { level, open, close };
      });
      const { pw, ...rest } = f;
      await api.post('/acad/terms', { ...Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, v || null])), custom: f.custom, priority_windows });
      toast.success('Term created'); setF({ ...f, code: '', name: '' }); qc.invalidateQueries({ queryKey: ['acad-terms'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { try { await api.delete(`/acad/terms/${id}`); qc.invalidateQueries({ queryKey: ['acad-terms'] }); } catch (e) { toast.error(errMsg(e)); } };
  const dateField = (k, label) => (<label className="text-xs text-slate-500">{label}<Input data-testid={`term-${k}`} type="date" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>);
  return (
    <div className="space-y-4">
      {canManage && <Card testid="term-setup">
        <p className="font-display font-bold mb-2">Set up a term</p>
        <div className="grid sm:grid-cols-3 gap-2 mb-2">
          <Input data-testid="term-code" placeholder="Code (e.g. 2026-S1)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
          <Input data-testid="term-name" placeholder="Name (e.g. Semester 1, 2026)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <Sel data-testid="term-type" value={f.term_type} onChange={(e) => setF({ ...f, term_type: e.target.value })}>
            {refValues(termTypes).map((v) => <option key={v}>{v}</option>)}
          </Sel>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {dateField('start', 'Starts')}{dateField('end', 'Ends')}
          {dateField('registration_open', 'Registration opens')}{dateField('registration_close', 'Registration closes')}
          {dateField('drop_add_open', 'Drop and add opens')}{dateField('drop_add_close', 'Drop and add closes')}
          {dateField('grading_open', 'Grading opens')}{dateField('grading_close', 'Grading closes')}
          {dateField('census_date', 'Census date')}
          <label className="text-xs text-slate-500">Status<Sel data-testid="term-status" className="w-full" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>{['draft', 'open', 'active', 'grading', 'closed'].map((s) => <option key={s}>{s}</option>)}</Sel></label>
        </div>
        <CustomFields entity="term" value={f.custom} onChange={(custom) => setF({ ...f, custom })} />
        <label className="text-xs text-slate-500 block mt-2">Priority registration windows - one per line: level, opens, closes (e.g. <span className="font-mono">12, 2026-07-01, 2026-07-15</span>). Levels register in their own window; leave empty to use the general dates.
          <textarea data-testid="term-priority-windows" className="w-full border border-slate-200 rounded-md text-sm p-2 mt-1 font-mono" rows={2}
            placeholder={'12, 2026-07-01, 2026-07-15\n9, 2026-07-08, 2026-07-15'}
            value={f.pw} onChange={(e) => setF({ ...f, pw: e.target.value })} />
        </label>
        <Button data-testid="term-create" className="mt-3 bg-slate-900 hover:bg-slate-800" disabled={!f.code || !f.name || !f.start || !f.end} onClick={save}>Create term</Button>
      </Card>}
      <Card testid="term-list">
        <p className="font-display font-bold mb-2">Terms</p>
        {(terms || []).map((t) => (
          <div key={t.id} className="border-b border-slate-50 py-2.5 flex items-start justify-between gap-3 flex-wrap" data-testid={`term-row-${t.code}`}>
            <div className="text-sm">
              <b>{t.code}</b> {t.name} <span className="text-xs text-slate-400">{t.term_type} · {t.start} to {t.end} · <span className="capitalize">{t.status}</span></span>
              <div className="flex gap-2 flex-wrap mt-1">
                {t.checklist.map((c) => <span key={c.label} className={`text-[10px] px-1.5 py-0.5 rounded ${c.done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>{c.done ? '✓ ' : '· '}{c.label}</span>)}
              </div>
            </div>
            <button data-testid={`term-delete-${t.code}`} onClick={() => del(t.id)} className="text-rose-500"><Trash size={15} /></button>
          </div>
        ))}
        {(terms || []).length === 0 && <p className="text-sm text-slate-400">No terms yet.</p>}
      </Card>
    </div>
  );
}

function Programmes() {
  const canManage = useCanManage();
  const qc = useQueryClient();
  const { data: levels } = useRef_('programme_levels');
  const { data: cats } = useRef_('course_categories');
  const { data: progs } = useQuery({ queryKey: ['acad-progs'], queryFn: () => api.get('/acad/programmes').then((r) => r.data) });
  const [f, setF] = useState({ code: '', name: '', level: 'Undergraduate', duration_terms: 8, credit_requirement: 120, awarding_body: '', rules: '', outcomes: '', custom: {} });
  const save = async () => {
    try {
      const curriculum_rules = f.rules.split('\n').filter(Boolean).map((ln) => {
        const [category, req, elec] = ln.split(',').map((x) => x.trim());
        return { category, required_credits: parseFloat(req) || 0, elective_credits: parseFloat(elec) || 0 };
      });
      await api.post('/acad/programmes', { code: f.code, name: f.name, level: f.level, duration_terms: parseInt(f.duration_terms, 10) || 1, credit_requirement: parseFloat(f.credit_requirement) || 0, awarding_body: f.awarding_body, curriculum_rules, outcomes: f.outcomes.split('\n').filter(Boolean), custom: f.custom });
      toast.success('Programme created'); setF({ ...f, code: '', name: '' }); qc.invalidateQueries({ queryKey: ['acad-progs'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {canManage && <Card testid="prog-form">
        <p className="font-display font-bold mb-2">Create a programme</p>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="prog-code" placeholder="Code (e.g. BSC-CS)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
            <Sel data-testid="prog-level" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })}>{refValues(levels).map((v) => <option key={v}>{v}</option>)}</Sel>
          </div>
          <Input data-testid="prog-name" placeholder="Name (e.g. B.Sc. Computer Science)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <div className="grid grid-cols-3 gap-2">
            <Input data-testid="prog-duration" type="number" placeholder="Terms" value={f.duration_terms} onChange={(e) => setF({ ...f, duration_terms: e.target.value })} />
            <Input data-testid="prog-credits" type="number" placeholder="Credits required" value={f.credit_requirement} onChange={(e) => setF({ ...f, credit_requirement: e.target.value })} />
            <Input data-testid="prog-awarding" placeholder="Awarding body" value={f.awarding_body} onChange={(e) => setF({ ...f, awarding_body: e.target.value })} />
          </div>
          <label className="text-xs text-slate-500">Curriculum rules - one per line: category, required credits, elective credits (categories: {refValues(cats).join(', ')})
            <textarea data-testid="prog-rules" rows={3} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder={'Core, 80, 0\nElective, 0, 40'} value={f.rules} onChange={(e) => setF({ ...f, rules: e.target.value })} /></label>
          <label className="text-xs text-slate-500">Learning outcomes - one per line
            <textarea data-testid="prog-outcomes" rows={2} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" value={f.outcomes} onChange={(e) => setF({ ...f, outcomes: e.target.value })} /></label>
          <CustomFields entity="programme" value={f.custom} onChange={(custom) => setF({ ...f, custom })} />
          <Button data-testid="prog-create" className="bg-slate-900 hover:bg-slate-800" disabled={!f.code || !f.name} onClick={save}>Create programme</Button>
        </div>
      </Card>}
      <Card testid="prog-list">
        <p className="font-display font-bold mb-2">Programmes</p>
        {(progs || []).map((p) => (
          <div key={p.id} className="border-b border-slate-50 py-2 text-sm" data-testid={`prog-row-${p.code}`}>
            <b>{p.code}</b> {p.name} <span className="text-xs text-slate-400">{p.level} · {p.duration_terms} term(s) · {p.credit_requirement} credits · {p.status}</span>
            {(p.curriculum_rules || []).length > 0 && <p className="text-xs text-slate-500">{p.curriculum_rules.map((r) => `${r.category}: ${r.required_credits} required + ${r.elective_credits} elective`).join(' · ')}</p>}
          </div>
        ))}
        {(progs || []).length === 0 && <p className="text-sm text-slate-400">No programmes yet.</p>}
      </Card>
    </div>
  );
}

function Courses() {
  const canManage = useCanManage();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data: cats } = useRef_('course_categories');
  const { data: schemes } = useRef_('grading_schemes');
  const { data: courses } = useQuery({ queryKey: ['acad-courses', q], queryFn: () => api.get('/acad/courses', { params: q ? { q } : {} }).then((r) => r.data) });
  const [f, setF] = useState({ code: '', title: '', credit_value: 4, contact_hours: 45, tuition_fee: '', grading_scheme: 'Letter grades A-F', category: 'Core', effective_from: new Date().toISOString().slice(0, 10), rules: '', custom: {} });
  const save = async () => {
    try {
      const rules = f.rules.split('\n').filter(Boolean).map((ln) => {
        const [type, course_code] = ln.split(',').map((x) => x.trim());
        return { type, course_code };
      });
      await api.post('/acad/courses', { ...f, credit_value: parseFloat(f.credit_value) || 0, contact_hours: parseFloat(f.contact_hours) || 0, tuition_fee: f.tuition_fee === '' ? null : parseFloat(f.tuition_fee) || 0, rules });
      toast.success('Course added to the catalogue'); setF({ ...f, code: '', title: '', rules: '' }); qc.invalidateQueries({ queryKey: ['acad-courses'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      {canManage && <Card testid="course-form">
        <p className="font-display font-bold mb-2">Add a course</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <Input data-testid="course-code" placeholder="Code (e.g. CS101)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
          <Input data-testid="course-title" className="sm:col-span-2" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Sel data-testid="course-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{refValues(cats).map((v) => <option key={v}>{v}</option>)}</Sel>
          <Input data-testid="course-credits" type="number" placeholder="Credits" value={f.credit_value} onChange={(e) => setF({ ...f, credit_value: e.target.value })} />
          <Input data-testid="course-hours" type="number" placeholder="Contact hours" value={f.contact_hours} onChange={(e) => setF({ ...f, contact_hours: e.target.value })} />
          <Input data-testid="course-fee" type="number" placeholder="Tuition fee (optional)" value={f.tuition_fee} onChange={(e) => setF({ ...f, tuition_fee: e.target.value })} />
          <Sel data-testid="course-scheme" value={f.grading_scheme} onChange={(e) => setF({ ...f, grading_scheme: e.target.value })}>{refValues(schemes).map((v) => <option key={v}>{v}</option>)}</Sel>
          <label className="text-xs text-slate-500">Effective from<Input data-testid="course-effective" type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></label>
        </div>
        <label className="text-xs text-slate-500 block mt-2">Rules - one per line: type, course code (types: prerequisite, corequisite, exclusive)
          <textarea data-testid="course-rules" rows={2} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" placeholder={'prerequisite, CS100'} value={f.rules} onChange={(e) => setF({ ...f, rules: e.target.value })} /></label>
        <CustomFields entity="course" value={f.custom} onChange={(custom) => setF({ ...f, custom })} />
        <Button data-testid="course-create" className="mt-2 bg-slate-900 hover:bg-slate-800" disabled={!f.code || !f.title} onClick={save}>Add to catalogue</Button>
      </Card>}
      <Card testid="course-list">
        <div className="flex items-center justify-between mb-2 gap-2">
          <p className="font-display font-bold">Course catalogue</p>
          <Input data-testid="course-search" className="w-56" placeholder="Search code or title" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {(courses || []).map((c) => (
          <div key={c.id} className="border-b border-slate-50 py-2 text-sm" data-testid={`course-row-${c.id}`}>
            <b>{c.code}</b> {c.title} <span className="text-xs text-slate-400">{c.credit_value} cr · {c.contact_hours} hrs · {c.grading_scheme}{c.tuition_fee ? ` · fee ${c.tuition_fee}` : ''} · effective {c.effective_from}{c.effective_to ? ` to ${c.effective_to}` : ''} · {c.status}</span>
            {(c.rules || []).length > 0 && <p className="text-xs text-slate-500">{c.rules.map((r) => `${r.type}: ${r.course_code}`).join(' · ')}</p>}
          </div>
        ))}
        {(courses || []).length === 0 && <p className="text-sm text-slate-400">The catalogue is empty.</p>}
      </Card>
    </div>
  );
}

function Sections() {
  const canManage = useCanManage();
  const qc = useQueryClient();
  const { data: modes } = useRef_('delivery_modes');
  const { data: terms } = useQuery({ queryKey: ['acad-terms'], queryFn: () => api.get('/acad/terms').then((r) => r.data) });
  const { data: courses } = useQuery({ queryKey: ['acad-courses', ''], queryFn: () => api.get('/acad/courses').then((r) => r.data) });
  const { data: staff } = useQuery({ queryKey: ['acad-staff'], queryFn: () => api.get('/acad/staff').then((r) => r.data).catch(() => []) });
  const { data: sections } = useQuery({ queryKey: ['acad-sections'], queryFn: () => api.get('/acad/sections').then((r) => r.data) });
  const [f, setF] = useState({ course_id: '', term_id: '', section_code: '', capacity: 30, waitlist_capacity: 5, delivery_mode: 'In person', room: '', day: 'mon', start: '09:00', end: '10:00', instructor: '', role: 'primary_instructor', custom: {} });
  const [conflicts, setConflicts] = useState([]);
  const save = async () => {
    try {
      const { data } = await api.post('/acad/sections', {
        course_id: f.course_id, term_id: f.term_id, section_code: f.section_code, capacity: parseInt(f.capacity, 10) || 30,
        waitlist_capacity: parseInt(f.waitlist_capacity, 10) || 0, delivery_mode: f.delivery_mode, room: f.room,
        meeting_pattern: [{ day: f.day, start: f.start, end: f.end }],
        allocations: f.instructor ? [{ user_id: f.instructor, role: f.role, workload: 1 }] : [],
        custom: f.custom,
      });
      setConflicts(data.conflicts || []);
      toast[data.conflicts?.length ? 'warning' : 'success'](data.conflicts?.length ? `Saved with ${data.conflicts.length} conflict(s) - review below` : 'Section scheduled');
      setF({ ...f, section_code: '' }); qc.invalidateQueries({ queryKey: ['acad-sections'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      {canManage && <Card testid="section-form">
        <p className="font-display font-bold mb-2">Schedule a section</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <Sel data-testid="section-course" value={f.course_id} onChange={(e) => setF({ ...f, course_id: e.target.value })}><option value="">Course...</option>{(courses || []).map((c) => <option key={c.id} value={c.id}>{c.code} {c.title}</option>)}</Sel>
          <Sel data-testid="section-term" value={f.term_id} onChange={(e) => setF({ ...f, term_id: e.target.value })}><option value="">Term...</option>{(terms || []).map((t) => <option key={t.id} value={t.id}>{t.code}</option>)}</Sel>
          <Input data-testid="section-code" placeholder="Section code (e.g. A)" value={f.section_code} onChange={(e) => setF({ ...f, section_code: e.target.value })} />
          <Sel data-testid="section-mode" value={f.delivery_mode} onChange={(e) => setF({ ...f, delivery_mode: e.target.value })}>{refValues(modes).map((v) => <option key={v}>{v}</option>)}</Sel>
          <Input data-testid="section-capacity" type="number" placeholder="Capacity" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} />
          <Input data-testid="section-waitlist" type="number" placeholder="Waitlist" value={f.waitlist_capacity} onChange={(e) => setF({ ...f, waitlist_capacity: e.target.value })} />
          <Input data-testid="section-room" placeholder="Room" value={f.room} onChange={(e) => setF({ ...f, room: e.target.value })} />
          <div className="flex gap-1">
            <Sel data-testid="section-day" value={f.day} onChange={(e) => setF({ ...f, day: e.target.value })}>{DAYS.map((d) => <option key={d}>{d}</option>)}</Sel>
            <Input data-testid="section-start" type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
            <Input data-testid="section-end" type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
          </div>
          <Sel data-testid="section-instructor" className="sm:col-span-2" value={f.instructor} onChange={(e) => setF({ ...f, instructor: e.target.value })}><option value="">Instructor (optional)...</option>{(staff || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Sel>
          <Sel data-testid="section-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="primary_instructor">Primary instructor</option><option value="co_instructor">Co-instructor</option><option value="assistant">Assistant</option></Sel>
        </div>
        <CustomFields entity="section" value={f.custom} onChange={(custom) => setF({ ...f, custom })} />
        <Button data-testid="section-create" className="mt-3 bg-slate-900 hover:bg-slate-800" disabled={!f.course_id || !f.term_id || !f.section_code} onClick={save}>Schedule section</Button>
        {conflicts.length > 0 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="section-conflicts">
            {conflicts.map((c, i) => <p key={`${c.type}-${c.with_section}-${i}`} className="text-xs text-amber-800 flex items-center gap-1"><WarningCircle size={13} /> {c.detail}</p>)}
          </div>
        )}
      </Card>}
      <Card testid="section-list">
        <p className="font-display font-bold mb-2">Sections</p>
        {(sections || []).map((s) => (
          <div key={s.id} className="border-b border-slate-50 py-2 text-sm" data-testid={`section-row-${s.id}`}>
            <b>{s.course_code} {s.section_code}</b> <span className="text-xs text-slate-400">{s.course_title} · {(s.meeting_pattern || []).map((m) => `${m.day} ${m.start}-${m.end}`).join(', ')} · {s.room || 'no room'} · {s.enrolled_count}/{s.capacity} enrolled · {s.delivery_mode}</span>
            {(s.allocations || []).length > 0 && <p className="text-xs text-slate-500">Faculty: {(s.allocations || []).map((a) => `${a.role.replace(/_/g, ' ')} (${a.workload})`).join(', ')}</p>}
          </div>
        ))}
        {(sections || []).length === 0 && <p className="text-sm text-slate-400">No sections scheduled yet.</p>}
      </Card>
    </div>
  );
}

function Timetable() {
  const [by, setBy] = useState('room');
  const [ref, setRef] = useState('');
  const [rows, setRows] = useState(null);
  const { data: sections } = useQuery({ queryKey: ['acad-sections'], queryFn: () => api.get('/acad/sections').then((r) => r.data) });
  const { data: staff } = useQuery({ queryKey: ['acad-staff'], queryFn: () => api.get('/acad/staff').then((r) => r.data).catch(() => []) });
  const load = async () => {
    try { setRows((await api.get('/acad/timetable', { params: { by, ref } })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="timetable-view">
      <p className="font-display font-bold mb-2">Timetable</p>
      <div className="flex gap-2 flex-wrap mb-3">
        <Sel data-testid="tt-by" value={by} onChange={(e) => { setBy(e.target.value); setRef(''); }}>
          <option value="room">By room</option><option value="faculty">By faculty</option><option value="section">By section</option><option value="student">By student</option>
        </Sel>
        {by === 'room' && <Input data-testid="tt-room" className="w-44" placeholder="Room name" value={ref} onChange={(e) => setRef(e.target.value)} />}
        {by === 'section' && <Sel data-testid="tt-section" value={ref} onChange={(e) => setRef(e.target.value)}><option value="">Section...</option>{(sections || []).map((s) => <option key={s.id} value={s.id}>{s.course_code} {s.section_code}</option>)}</Sel>}
        {by === 'faculty' && <Sel data-testid="tt-faculty" value={ref} onChange={(e) => setRef(e.target.value)}><option value="">Faculty...</option>{(staff || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</Sel>}
        {by === 'student' && <Input data-testid="tt-student" className="w-56" placeholder="Student ID" value={ref} onChange={(e) => setRef(e.target.value)} />}
        <Button data-testid="tt-load" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!ref} onClick={load}>Show timetable</Button>
      </div>
      {rows && rows.length === 0 && <p className="text-sm text-slate-400">Nothing scheduled for this view{by === 'student' ? ' (students appear once registration opens in a later phase)' : ''}.</p>}
      {rows && rows.length > 0 && (
        <div data-testid="tt-rows">{DAYS.filter((d) => rows.some((r) => r.day === d)).map((d) => (
          <div key={d} className="mb-2">
            <p className="font-mono-label text-[10px] uppercase text-slate-400">{d}</p>
            {rows.filter((r) => r.day === d).map((r) => <p key={`${r.section_id}-${r.start}`} className="text-sm py-0.5">{r.start}-{r.end} · <b>{r.course_code} {r.section_code}</b> {r.course_title} · {r.room || r.delivery_mode}</p>)}
          </div>
        ))}</div>
      )}
    </Card>
  );
}

function RefData() {
  const qc = useQueryClient();
  const kinds = ['grading_schemes', 'term_types', 'delivery_modes', 'student_statuses', 'academic_standings', 'programme_levels', 'course_categories'];
  const [kind, setKind] = useState('grading_schemes');
  const { data } = useRef_(kind);
  const [newVal, setNewVal] = useState('');
  const saveValues = async (values) => {
    try { await api.put(`/acad/refdata/${kind}`, { values }); qc.invalidateQueries({ queryKey: ['ref', kind] }); toast.success('Reference data saved'); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="refdata-panel">
      <p className="font-display font-bold mb-1">Reference data</p>
      <p className="text-xs text-slate-500 mb-3">Everything here is configuration - versioned, effective-dated, never hard-coded.</p>
      <div className="flex gap-2 flex-wrap mb-3">
        {kinds.map((k) => <button key={k} data-testid={`ref-kind-${k}`} onClick={() => setKind(k)} className={`px-2.5 py-1 rounded-full text-xs ${kind === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>{k.replace(/_/g, ' ')}</button>)}
      </div>
      {(data?.values || []).map((v, i) => (
        <div key={v.value} className="flex items-center gap-2 border-b border-slate-50 py-1.5 text-sm">
          <span className={v.active ? '' : 'line-through text-slate-400'}>{v.value}</span>
          <span className="text-[10px] text-slate-400">effective {v.effective_from}</span>
          <button data-testid={`ref-toggle-${i}`} onClick={() => saveValues(data.values.map((x, j) => j === i ? { ...x, active: !x.active } : x))}
            className="ms-auto text-xs text-orange-600 underline">{v.active ? 'Retire' : 'Restore'}</button>
        </div>
      ))}
      <div className="flex gap-2 mt-3">
        <Input data-testid="ref-new-value" placeholder="Add a value" value={newVal} onChange={(e) => setNewVal(e.target.value)} />
        <Button data-testid="ref-add" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!newVal.trim()} onClick={() => { saveValues([...(data?.values || []), { value: newVal.trim(), effective_from: new Date().toISOString().slice(0, 10), active: true }]); setNewVal(''); }}>Add value</Button>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Version {data?.version}</p>
    </Card>
  );
}

export default function AcademicCore() {
  const { t } = useI18n();
  const [tab, setTab] = useState('overview');
  const TABS = [['overview', 'Overview'], ['hierarchy', 'Hierarchy'], ['terms', 'Terms'], ['programmes', 'Programmes'], ['courses', 'Courses'], ['sections', 'Sections'], ['registration', 'Registration'], ['grading', 'Grading'], ['timetable', 'Timetable'], ['refdata', 'Reference data'], ['custom', 'Customization']];
  return (
    <div className="space-y-5" data-testid="academic-core-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight flex items-center gap-2"><GraduationCap size={26} className="text-orange-600" />{t('Academic core')}</h1>
        <p className="text-sm text-slate-500">{t('Terms, programmes, courses and sections - one configurable model for schools and universities.')}</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {TABS.map(([k, l]) => <button key={k} data-testid={`acad-tab-${k}`} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-sm ${tab === k ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>{t(l)}</button>)}
      </div>
      {tab === 'overview' && <Overview setTab={setTab} />}
      {tab === 'hierarchy' && <HierarchyTab />}
      {tab === 'terms' && <Terms />}
      {tab === 'programmes' && <Programmes />}
      {tab === 'courses' && <Courses />}
      {tab === 'sections' && <Sections />}
      {tab === 'registration' && <RegistrationTab />}
      {tab === 'grading' && <GradingTab />}
      {tab === 'timetable' && <Timetable />}
      {tab === 'refdata' && <RefData />}
      {tab === 'custom' && <CustomizationTab />}
    </div>
  );
}
